import React from 'react';
import { useSelector } from 'react-redux';
import { Activity, RefreshCcw, Truck, Package } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { DropdownMenu } from '../../../../../components/ui/DropdownMenu';
import { selectHasFulfillmentPro } from '../../../../../features/auth/authSelectors';

/**
 * The bulk action bar, plus the menu that holds the rest.
 *
 * Same shape as the one in Envíos — the counter and the buttons to the left,
 * "Opciones" to the right, all inside a row with a floor height so it does not
 * jump when the bar appears. Two things from that file are deliberately not
 * copied: its private `ActionButton` (this uses the shared `Button`) and its
 * `animate-in` classes, which do not compile because the plugin was never
 * installed.
 *
 * @param {object} props
 * @param {number} props.selectedCount
 * @param {() => void} props.onChangeStatus
 * @param {() => void} props.onGetTracking
 * @param {() => void} props.onChangeCourier
 */
export const AttentionActions = ({
  selectedCount,
  onChangeStatus,
  onGetTracking,
  onChangeCourier,
}) => {
  // Changing the courier is behind Fulfillment Pro here too. This is the screen
  // somebody opens precisely because a shipment went wrong, so leaving it open
  // would hand over what the addon sells at the moment it is most wanted.
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);
  const hasSelection = selectedCount > 0;

  const bulkActions = [
    {
      label: 'Cambiar estado',
      icon: Activity,
      onClick: onChangeStatus,
      disabled: !hasSelection,
      description: hasSelection ? undefined : 'Selecciona envíos primero',
    },
    {
      label: 'Obtener trackings',
      icon: RefreshCcw,
      onClick: onGetTracking,
      disabled: !hasSelection,
      description: hasSelection ? undefined : 'Selecciona envíos primero',
    },
    ...(hasFulfillmentPro
      ? [{
        label: 'Cambiar courier',
        icon: Truck,
        onClick: onChangeCourier,
        disabled: !hasSelection,
        description: hasSelection
          ? 'Solo se aplica a los que aún no salieron'
          : 'Selecciona envíos primero',
      }]
      : []),
  ];

  return (
    <div className="min-h-[48px] flex items-center justify-between gap-3">
      {hasSelection ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 text-ninja-green font-bold text-sm px-2">
            <Package size={16} />
            <span>
              {selectedCount} {selectedCount === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
          </div>

          <Button variant="outline" size="sm" icon={Activity} onClick={onChangeStatus}>
            Cambiar estado
          </Button>
          <Button variant="outline" size="sm" icon={RefreshCcw} onClick={onGetTracking}>
            Obtener trackings
          </Button>
          {hasFulfillmentPro && (
            <Button variant="primary" size="sm" icon={Truck} onClick={onChangeCourier}>
              Cambiar courier
            </Button>
          )}
        </div>
      ) : (
        <span className="text-sm text-text-muted">
          Selecciona envíos para actuar sobre varios a la vez
        </span>
      )}

      <DropdownMenu triggerLabel="Opciones" items={bulkActions} />
    </div>
  );
};
